import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

interface ScreenSkeletonProps {
  /** Number of field-shaped blocks to reserve (default 4 - a typical capture screen). */
  fields?: number;
  /** Show the screen-heading block (title + subtitle lines). */
  heading?: boolean;
  /**
   * Delay before the skeleton becomes visible. Warm IndexedDB loads resolve in
   * 10-80ms; painting a skeleton for 1-3 frames and then swapping it reads as
   * flicker, not loading. Below the delay the screen stays blank (one clean paint
   * when content lands); only genuinely slow loads ever show the skeleton.
   * Pass 0 to show immediately (e.g. known-slow first-sync paths).
   */
  appearDelayMs?: number;
}

/**
 * Layout-stable loading state for a screen: reserves REALISTIC vertical space while
 * data loads, so the arriving content replaces same-sized blocks instead of a
 * zero-height body.
 *
 * THE PATTERN (layout stability): never swap in a bare centred LoadingIndicator for a
 * whole screen - the centred spinner is absolutely positioned, so the page body
 * renders at ~zero height and the real content then lands in one frame as a
 * full-viewport layout shift. Swap same-shaped skeletons for content instead.
 * (Enforced by tests/layout/loadingIdiomRatchet.test.ts.)
 */
export function ScreenSkeleton({
  fields = 4,
  heading = true,
  appearDelayMs = 150,
}: ScreenSkeletonProps) {
  const [visible, setVisible] = useState(appearDelayMs <= 0);

  useEffect(() => {
    if (appearDelayMs <= 0) return;
    const timer = setTimeout(() => setVisible(true), appearDelayMs);
    return () => clearTimeout(timer);
  }, [appearDelayMs]);

  if (!visible) {
    // Same container box, no shimmer yet: fast loads replace this with content in
    // a single paint; slow loads grow the skeleton into it without a scroll jump.
    return (
      <div
        className="min-h-[60vh] w-full p-4"
        data-testid="screen-skeleton-pending"
        aria-busy="true"
      />
    );
  }

  return (
    <div className="w-full p-4" data-testid="screen-skeleton" aria-busy="true">
      {heading && (
        <div className="py-2">
          <Skeleton width={140} height={16} />
          <Skeleton width={220} height={28} className="mt-1" />
        </div>
      )}
      {Array.from({ length: fields }, (_, i) => (
        <div key={i} className="py-4">
          <Skeleton width={180} height={16} />
          <Skeleton height={40} className="mt-2" />
        </div>
      ))}
    </div>
  );
}
